import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { useAuthStore } from '@/store/auth.store'
import { useUpdateUsername } from '../hooks/useProfile'
import { usernameSchema, type UsernameFormData } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

export function UsernameForm() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { mutate: update, isPending } = useUpdateUsername()
  const [apiError, setApiError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<UsernameFormData>({
    resolver: zodResolver(usernameSchema),
    defaultValues: { username: user?.username ?? '' },
  })

  const onSubmit = (data: UsernameFormData) => {
    setApiError(null)
    update(data, {
      onSuccess: () => {
        setSuccessMsg(t('profile.username_updated'))
        setTimeout(() => setSuccessMsg(null), 3000)
      },
      onError: (err) => {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          setApiError(t('profile.username_cooldown'))
        } else {
          setApiError(t('profile.generic_error'))
        }
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <h2 className="text-base font-semibold text-neutral-900">{t('profile.username_title')}</h2>

      {successMsg && <Alert variant="success">{successMsg}</Alert>}
      {apiError && <Alert variant="error">{apiError}</Alert>}

      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <Input
            label={t('profile.username_label')}
            placeholder={t('profile.username_placeholder')}
            error={errors.username?.message && t(errors.username.message)}
            helperText={t('profile.username_hint')}
            {...register('username')}
          />
        </div>
        <Button type="submit" isLoading={isPending} disabled={!isDirty} className="flex-shrink-0">
          {t('profile.username_change')}
        </Button>
      </div>
    </form>
  )
}
